/**
 * `<page-tests>` — policy test suites and their reports, routed at
 * `/instances/:id/tests`.
 *
 * It is both halves of the testing story: authoring a suite without ever seeing
 * `policy test`'s YAML dialect (`SC-F706`), and running it as a one-shot task on
 * the instance's pinned image with the report rendered faithfully (`SC-F707`,
 * `SC-F700`, `SC-F332`). Authoring is MVP for a blunt reason — nothing else in
 * the product creates a suite, so a Run button would have had nothing to run.
 *
 * Stories: `SC-S706`, `SC-S707`, `SC-S708`, `SC-S709`, and the input half of
 * `SC-S713`'s optional test gate.
 *
 * **Four honesty requirements, and they are the reason this page is more than a
 * table of green ticks:**
 *
 *  1. **Exit 3 covers ZERO CASES RUN as well as failures.** A suite that matched
 *     no files is a failure, not a pass, and the report says which of the three
 *     conditions it was — `no_cases`, `case_failures` or `bundle_invalid`. An
 *     empty suite is also refused here at authoring time, because discovering it
 *     as a red gate in front of a publish is discovering it too late.
 *  2. **A case that exercises `request.*` passes here and fails in
 *     production.** The harness populates request facts; the live REST and MCP
 *     paths do not (`SLUICE-U12`), and the production failure is a *denial*,
 *     because a request template that cannot render denies the query. That
 *     warning is a full-width alert on the report and a badge on the case, and a
 *     passing run carries the annotation that the pass is qualified.
 *  3. **List assertions compare as sorted SETS and `rewrittenSql` compares
 *     whitespace-normalised.** The diff therefore reports `missing` and
 *     `unexpected` separately — never "partially matched" — and shows both
 *     normalised forms so a whitespace difference is visibly not the cause.
 *  4. **A case's identity is JWT-shaped and cannot be otherwise.** A
 *     `subjects.apiKeys` selector is unreachable in `policy test` exactly as it
 *     is in `policy explain`, so a suite that appears to cover the API-key path
 *     does not (`SLUICE-U27`). Every run carries that caveat.
 *
 * The failure strings the runner emits are preformatted human text, so `raw` is
 * always shown alongside the parsed diff: when a future Sluice reworders one, the
 * parser degrades to `unparsed` and the raw line is the only thing guaranteed to
 * be what the runner said (`SLUICE-U25`).
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import { me } from "../api/auth.js";
import {
  createTestSuite,
  deleteTestSuite,
  explainPrefill,
  listTestSuites,
  replaceTestSuite,
  runTests,
  TEST_OUTCOMES,
  type AssertionDiff,
  type SuiteCase,
  type TestCaseResult,
  type TestRunResult,
  type TestSuite,
  type ValidationFinding,
  type ValidationTarget,
} from "../api/cli.js";
import { flattenSchema, getSchema } from "../api/datasources.js";
import { ApiError, errorDetail, parseError, validationFields } from "../api/errors.js";
import { getInstance, type Instance } from "../api/instances.js";
import { cancelJob, isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import { getPolicyFormSchema, type PolicyFormSchema } from "../api/policies.js";
import { atLeast, type Role } from "../api/orgs.js";
import { hrefFor, navigate } from "../nav.js";

const WAIT_SECONDS = 20;

/** Every explain- and test-shaped surface carries it (`SLUICE-U27`). */
const JWT_ONLY_BADGE = "JWT subject semantics only";

/** The suite name becomes a file name inside the rendered bundle, so it is an
 * RFC-1123 label — the same grammar the route enforces, restated here so the
 * refusal happens in the form rather than after a round trip. */
const SUITE_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** The `schema:` block's keys must be three-part `catalog.schema.table` names:
 * the runner builds its static schema cache from them and refuses anything
 * else, and the failure surfaces as a message about a fixture rather than about
 * the typo that caused it. */
const TABLE_FQN = /^[^.\s]+\.[^.\s]+\.[^.\s]+$/;

const TARGET_OPTIONS: BnSelectOption[] = [
  { value: "draft", label: "Draft", description: "The bundle the editors are changing." },
  { value: "live", label: "Live generation", description: "The bundle the instance is serving." },
  {
    value: "generation",
    label: "A generation number",
    description: "A bundle from the history, for checking a regression against what was live.",
  },
];

/** `expect.outcome`'s closed set plus "not asserted", because an empty field is
 * not checked at all — which is a third state and not a passing assertion. */
const OUTCOME_OPTIONS: BnSelectOption[] = [
  {
    value: "",
    label: "Not asserted",
    description: "The runner does not compare the outcome at all.",
  },
  ...TEST_OUTCOMES.map((o) => ({
    value: o,
    label: o,
    description:
      o === "allow"
        ? "The query is permitted, possibly with filters and masks."
        : o === "deny"
          ? "The query is refused — by a policy, or by default-deny."
          : "A rejection rule refused the query.",
  })),
];

const SUITE_COLUMNS: BnColumn[] = [
  { key: "name", label: "Suite" },
  { key: "cases", label: "Cases", width: "1px", align: "right" },
  { key: "gate", label: "Publish gate", width: "1px" },
  { key: "risk", label: "" },
  { key: "updated", label: "Updated", width: "1px" },
  { key: "actions", label: "", width: "1px", align: "right" },
];

const CASE_COLUMNS: BnColumn[] = [
  { key: "name", label: "Case" },
  { key: "subject", label: "Subject" },
  { key: "asserts", label: "Asserts" },
  { key: "risk", label: "" },
  { key: "actions", label: "", width: "1px", align: "right" },
];

/** An editable key/value pair. A list rather than a map so a half-typed key
 * cannot collide with another row and silently drop it. */
interface KeyValue {
  key: string;
  value: string;
}

/** One case, in the shape the form edits. It is converted to and from the API's
 * shape at the boundary, so the wire type stays the contract and the draft type
 * stays convenient. */
interface CaseDraft {
  name: string;
  subject: string;
  issuer: string;
  email: string;
  groupsText: string;
  claims: KeyValue[];
  useRequest: boolean;
  headers: KeyValue[];
  userAgent: string;
  sql: string;
  outcome: string;
  denyPolicy: string;
  errorCode: string;
  rejections: string[];
  filters: string[];
  masks: string[];
  postMasks: string[];
  applied: string[];
  rewrittenSql: string;
  rewrittenSqlContains: string[];
}

/** One suite, in the shape the form edits. */
interface SuiteDraft {
  id?: string;
  name: string;
  enabled: boolean;
  /** The fixture map as editable rows: a three-part table name and its columns. */
  schema: Array<{ fqn: string; columns: string }>;
  cases: CaseDraft[];
}

/**
 * `<page-tests>` authors policy test suites and renders their runs.
 *
 * Implements `SC-F706` (suite and case authoring, so the product never asks
 * anyone to write `policy test`'s dialect), `SC-F707` (the runner and its report,
 * including the three exit-3 conditions and the request-facts warning),
 * `SC-F700`, `SC-F332`, and the opt-in half of `SC-F709`'s test gate.
 *
 * Stories: `SC-S706`, `SC-S707`, `SC-S708`, `SC-S709`, `SC-S713`.
 *
 *   <page-tests .instanceId=${id}></page-tests>
 */
@customElement("page-tests")
export class PageTests extends LitElement {
  @property() instanceId = "";

  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private loadError: ApiError | null = null;
  @state() private orgRole: Role | undefined;

  @state() private suites: TestSuite[] = [];
  @state() private suitesLoading = true;
  @state() private suitesError: ApiError | null = null;

  /** The suite being authored, or null when the list is just being read. */
  @state() private draft: SuiteDraft | null = null;
  @state() private draftErrors: Record<string, string> = {};
  @state() private saving = false;
  @state() private saveError: ApiError | null = null;

  /** The case open in the drawer, and its index in the draft — null for a new
   * one. */
  @state() private caseDraft: CaseDraft | null = null;
  @state() private caseIndex: number | null = null;
  @state() private caseErrors: Record<string, string> = {};

  /** `expect.errorCode` is offered from the form schema's own `ErrorCode` enum
   * (frozen interface `I3`), never from a list typed out here: a drift check
   * defends the schema and nothing would defend a copy. Empty means the schema
   * could not be read, and the field degrades to free text rather than blocking
   * authoring. */
  @state() private errorCodes: BnSelectOption[] = [];

  @state() private targetKind: "draft" | "live" | "generation" = "draft";
  @state() private generation = "";

  @state() private job: Job<TestRunResult> | null = null;
  @state() private result: TestRunResult | null = null;
  @state() private runError: ApiError | null = null;
  @state() private running = false;
  /** The suite the report on screen was produced from, so "explain this case"
   * can rebuild that case's identity and statement. */
  @state() private ranSuite: TestSuite | null = null;
  /** Which failing cases are expanded. */
  @state() private openCases = new Set<string>();

  /** The suite a delete has been asked for. Deleting a suite takes its stored
   * reports with it, so it is confirmed rather than done on one click. */
  @state() private confirmDelete: TestSuite | null = null;

  private abort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      margin-bottom: var(--bino-space-lg);
    }
    .head-line {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: var(--bino-space-md) 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    h2:first-child {
      margin-top: 0;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
    }
    .grid {
      display: grid;
      gap: var(--bino-space-md);
      grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    }
    .row bn-field,
    .grid bn-field {
      margin-bottom: 0;
    }
    .narrow {
      width: 15rem;
    }
    .repeater {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .repeater-row {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
    }
    .repeater-row bn-input {
      flex: 1 1 8rem;
      min-width: 0;
    }
    fieldset {
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      margin: 0 0 var(--bino-space-md);
    }
    legend {
      padding: 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      color: var(--bino-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      max-width: 80ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    ul {
      margin: 0 0 var(--bino-space-sm);
      padding-left: var(--bino-space-lg);
      max-width: 80ch;
    }
    li {
      margin-bottom: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .verdict {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-sm);
    }
    .verdict-line {
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .alert {
      border: 1px solid var(--bino-warning-border);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .alert p {
      color: var(--bino-warning-text);
    }
    .alert-title {
      font-weight: 700;
      margin-bottom: var(--bino-space-xs);
    }
    .case {
      border-top: 1px solid var(--bino-border-light);
      padding: var(--bino-space-md) 0;
    }
    .case:first-of-type {
      border-top: none;
      padding-top: 0;
    }
    .case-head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .case-name {
      font-weight: 600;
    }
    .diff {
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      border: 1px solid var(--bino-border-light);
    }
    .diff-head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-sm);
    }
    .sides {
      display: grid;
      gap: var(--bino-space-sm);
      grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
    }
    .side {
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-sm);
      background: var(--bino-surface);
      border: 1px solid var(--bino-border-light);
    }
    .side-label {
      font-size: var(--bino-font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .want {
      background: var(--bino-diff-del-bg);
      color: var(--bino-diff-del-text);
    }
    .got {
      background: var(--bino-diff-add-bg);
      color: var(--bino-diff-add-text);
    }
    .mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      white-space: pre-wrap;
      word-break: break-word;
    }
    .chip {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text-muted);
    }
    .raw {
      margin-top: var(--bino-space-sm);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      overflow-x: auto;
      white-space: pre;
    }
    a.inline {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has("instanceId") && changed.get("instanceId") !== undefined) void this.load();
  }

  private async load() {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.instance = await getInstance(this.instanceId);
      this.loadError = null;
    } catch (err) {
      this.loadError = parseError(err);
      this.loading = false;
      return;
    }
    this.loading = false;
    void this.loadRole();
    void this.loadSuites();
    void this.loadErrorCodes();
  }

  /** Authoring a suite needs maintainer; running one needs member. An unknown
   * role pre-disables nothing — the server re-checks every route and answers
   * `forbidden` with the role it wanted. */
  private async loadRole() {
    const org = this.instance?.org;
    if (!org) return;
    try {
      const identity = await me();
      this.orgRole = identity.organizations.find((o) => o.id === org)?.role;
    } catch {
      this.orgRole = undefined;
    }
  }

  private get mayAuthor(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "maintainer");
  }

  private async loadSuites() {
    this.suitesLoading = true;
    try {
      const page = await listTestSuites(this.instanceId);
      this.suites = page.items;
      this.suitesError = null;
    } catch (err) {
      this.suitesError = parseError(err);
      this.suites = [];
    } finally {
      this.suitesLoading = false;
    }
  }

  /**
   * The `ErrorCode` enum, read from the form schema rather than typed out here.
   *
   * The pinned handler serves `internal/policymodel`'s embedded document
   * verbatim, while the api module's type models `06` §4.9's envelope around it,
   * so both shapes are accepted and neither is assumed.
   */
  private async loadErrorCodes() {
    try {
      const res = await getPolicyFormSchema();
      const doc: PolicyFormSchema = res.schema ?? (res as unknown as PolicyFormSchema);
      const enums = (doc?.enums ?? {}) as Record<
        string,
        { values?: Array<{ value: string; label?: string; help?: string; status?: string; reason?: string }> }
      >;
      const values = enums["ErrorCode"]?.values ?? [];
      if (values.length === 0) return;
      // Whether a value may be offered is the SCHEMA's decision, not this
      // page's: each status carries its own `offer` and `badge`, so a value that
      // the pinned build refuses is disabled with the schema's reason and a value
      // the schema hides is not listed. Deciding it here would be a second,
      // silently diverging copy of that judgement.
      const offer = new Map((doc?.statuses ?? []).map((s) => [s.status as string, s.offer]));
      const badge = new Map((doc?.statuses ?? []).map((s) => [s.status as string, s.badge]));
      this.errorCodes = [
        {
          value: "",
          label: "Not asserted",
          description: "The runner does not compare the error code at all.",
        },
        ...values
          .filter((v) => !v.status || offer.get(v.status) !== "hidden")
          .map((v) => ({
            value: v.value,
            label: v.label || v.value,
            description: [v.help, v.status ? badge.get(v.status) : ""]
              .filter((s) => s)
              .join(" · "),
            disabled: v.status !== undefined && offer.get(v.status) === "no",
            disabledReason: v.reason,
          })),
      ];
    } catch {
      // Free text is a worse field, not a blocked one.
      this.errorCodes = [];
    }
  }

  /* ── authoring ─────────────────────────────────────────────────────────── */

  private newDraft(): SuiteDraft {
    return { name: "", enabled: false, schema: [], cases: [] };
  }

  private startCreate() {
    this.draft = this.newDraft();
    this.draftErrors = {};
    this.saveError = null;
  }

  private startEdit(suite: TestSuite) {
    this.draft = toSuiteDraft(suite);
    this.draftErrors = {};
    this.saveError = null;
  }

  private validateDraft(d: SuiteDraft): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!SUITE_NAME.test(d.name)) {
      errs["name"] =
        "1–63 characters of lower-case letters, digits and hyphens, starting and ending alphanumeric: the name becomes a file name inside the rendered bundle.";
    }
    if (d.cases.length === 0) {
      errs["cases"] =
        "A suite needs at least one case. `policy test` exits 3 when zero cases run, so an empty suite would publish as a failing gate.";
    }
    const seen = new Set<string>();
    d.cases.forEach((c, i) => {
      if (seen.has(c.name)) errs[`cases.${i}`] = `${c.name} is used by more than one case.`;
      seen.add(c.name);
    });
    d.schema.forEach((row, i) => {
      if (!TABLE_FQN.test(row.fqn)) {
        errs[`schema.${i}`] =
          "A three-part catalog.schema.table name. The runner refuses anything else.";
      } else if (row.columns.trim() === "") {
        errs[`schema.${i}`] = "List the columns, or remove the row.";
      }
    });
    return errs;
  }

  private async saveDraft() {
    const d = this.draft;
    if (!d) return;
    const errs = this.validateDraft(d);
    this.draftErrors = errs;
    if (Object.keys(errs).length > 0) return;
    this.saving = true;
    this.saveError = null;
    try {
      const body = fromSuiteDraft(d);
      if (d.id) await replaceTestSuite(this.instanceId, d.id, body);
      else await createTestSuite(this.instanceId, body);
      toast(d.id ? "Suite saved" : "Suite created", "success");
      this.draft = null;
      await this.loadSuites();
    } catch (err) {
      const parsed = parseError(err);
      this.saveError = parsed;
      const fields = validationFields(parsed);
      if (fields.length > 0) {
        // The API's paths are `cases[3].sql`-shaped; they are shown as-is beside
        // the form rather than guessed into a control, because pointing at the
        // wrong field is worse than pointing at none.
        this.draftErrors = Object.fromEntries(fields.map((f) => [f.path, f.reason]));
      }
    } finally {
      this.saving = false;
    }
  }

  private async removeSuite(suite: TestSuite) {
    if (!suite.id) return;
    this.confirmDelete = null;
    try {
      await deleteTestSuite(this.instanceId, suite.id);
      toast(`Suite ${suite.name} deleted`, "success");
      if (this.ranSuite?.id === suite.id) {
        this.result = null;
        this.ranSuite = null;
      }
      await this.loadSuites();
    } catch (err) {
      this.suitesError = parseError(err);
    }
  }

  /** Fills the fixture map from the introspection cache (`SC-F411`), which is
   * what `SC-F706` intends: the `schema:` block exists so `SELECT *` can expand
   * with no live datasource, and typing it out by hand is not authoring. A
   * degraded cache is reported and does not block — free text is always
   * available. */
  private async fillSchemaFromCache() {
    const d = this.draft;
    if (!d) return;
    try {
      const resp = await getSchema(this.instanceId);
      const tables = flattenSchema(resp);
      if (tables.length === 0) {
        toast(
          resp.status.warning ??
            "The schema cache holds nothing for this instance yet, so there is nothing to fill in.",
          "warning",
        );
        return;
      }
      const existing = new Set(d.schema.map((r) => r.fqn));
      const rows = [...d.schema];
      for (const t of tables) {
        const fqn = `${t.catalog}.${t.schema}.${t.table}`;
        if (existing.has(fqn)) continue;
        rows.push({ fqn, columns: t.columns.map((c) => c.name).join(" ") });
      }
      this.draft = { ...d, schema: rows };
      if (resp.status.degraded || !resp.status.fresh) {
        toast(
          resp.status.warning ??
            "The schema cache is stale, so the fixture may not match the live tables.",
          "warning",
        );
      } else {
        toast(`${tables.length} table(s) added from the schema cache`, "success");
      }
    } catch (err) {
      toast(parseError(err).message, "error");
    }
  }

  /* ── the case drawer ───────────────────────────────────────────────────── */

  private openCase(index: number | null) {
    this.caseErrors = {};
    this.caseIndex = index;
    this.caseDraft =
      index === null ? emptyCaseDraft() : { ...(this.draft?.cases[index] ?? emptyCaseDraft()) };
  }

  private validateCase(c: CaseDraft): Record<string, string> {
    const errs: Record<string, string> = {};
    if (c.name.trim() === "") errs["name"] = "Required: the report identifies a case by its name.";
    if (c.subject.trim() === "") errs["subject"] = "Required.";
    if (c.sql.trim() === "") errs["sql"] = "Required: a case evaluates one statement.";
    for (const g of splitGroups(c.groupsText)) {
      if (g.includes(",")) {
        errs["groups"] = "A group may not contain a comma. Separate groups with spaces.";
      }
    }
    c.claims.forEach((kv, i) => {
      if (kv.key.trim() === "" && kv.value.trim() === "") return;
      if (kv.key.trim() === "") errs[`claims.${i}`] = "A claim needs a name.";
      else if (/[,=]/.test(kv.key)) {
        errs[`claims.${i}`] = "A claim name may not contain a comma or an equals sign.";
      } else if (kv.value.includes(",")) {
        errs[`claims.${i}`] = "A claim value may not contain a comma.";
      }
    });
    if (c.outcome !== "" && !(TEST_OUTCOMES as readonly string[]).includes(c.outcome)) {
      errs["outcome"] = "One of allow, deny or reject.";
    }
    return errs;
  }

  private commitCase() {
    const c = this.caseDraft;
    const d = this.draft;
    if (!c || !d) return;
    const errs = this.validateCase(c);
    this.caseErrors = errs;
    if (Object.keys(errs).length > 0) return;
    const cases = [...d.cases];
    if (this.caseIndex === null) cases.push(c);
    else cases[this.caseIndex] = c;
    this.draft = { ...d, cases };
    this.caseDraft = null;
    this.caseIndex = null;
    // Every case-level complaint is now stale — the set of cases just changed.
    this.draftErrors = Object.fromEntries(
      Object.entries(this.draftErrors).filter(([k]) => !k.startsWith("cases")),
    );
  }

  /** The case-level complaints, as sentences. They cannot anchor to a control —
   * a case is a row in a table and the API's own paths are `cases[3].sql`-shaped
   * — so they are listed where the cases are, which is the object they are
   * about. */
  private caseComplaints(): string[] {
    return Object.entries(this.draftErrors)
      .filter(([k]) => k === "cases" || k.startsWith("cases"))
      .map(([k, v]) => (k === "cases" ? v : `${k} — ${v}`));
  }

  private patchCase(patch: Partial<CaseDraft>) {
    if (!this.caseDraft) return;
    this.caseDraft = { ...this.caseDraft, ...patch };
  }

  /* ── running ───────────────────────────────────────────────────────────── */

  private get target(): ValidationTarget {
    if (this.targetKind === "generation") return Number(this.generation);
    return this.targetKind;
  }

  /** Runs a stored suite by id, or the unsaved draft inline. Inline is what the
   * editor's "run without saving" does; a stored id is what CI does, and it is
   * the form a publish gate can point at. */
  private async run(input: { suite?: TestSuite; suiteId?: string; label: TestSuite }) {
    if (this.targetKind === "generation" && !/^[1-9][0-9]*$/.test(this.generation.trim())) {
      this.runError = new ApiError({
        code: "validation_failed",
        message: "Name a positive generation number to run against.",
      });
      return;
    }
    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.runError = null;
    this.result = null;
    this.job = null;
    this.ranSuite = input.label;
    this.openCases = new Set();
    try {
      const started = await runTests(
        this.instanceId,
        { target: this.target, suite: input.suite, suite_id: input.suiteId },
        { waitSeconds: WAIT_SECONDS },
      );
      this.job = started.job;
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<TestRunResult>(started.job.id, {
            waitSeconds: WAIT_SECONDS,
            onUpdate: (j) => (this.job = j),
            signal: this.abort.signal,
          });
      this.job = done;
      const failure = jobFailure(done);
      if (failure) {
        this.runError = failure;
        return;
      }
      this.result = done.result;
      // Failing cases open by default: the diff is the reason the reader came.
      this.openCases = new Set(
        (done.result?.cases ?? []).filter((c) => !c.passed).map((c) => c.name),
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      this.runError = parseError(err);
    } finally {
      this.running = false;
    }
  }

  private async cancel() {
    const id = this.job?.id;
    if (!id) return;
    try {
      await cancelJob(id);
    } catch (err) {
      this.runError = parseError(err);
    }
  }

  /* ── rendering ─────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading && !this.instance) {
      return html`<bn-spinner block size="28" label="Loading this instance"></bn-spinner>`;
    }
    if (this.loadError) return this.renderLoadError(this.loadError);
    return html`
      <header>
        <div class="head-line">
          <h1>Policy tests</h1>
          <bn-badge variant="warning">${JWT_ONLY_BADGE}</bn-badge>
        </div>
        <p class="lede">
          A suite is a set of cases — an identity, a statement and what you expect the engine to do
          with it. It runs inside a one-shot container on this instance's pinned image, against the
          bundle you choose, so a pass here is a statement about the binary that will serve the
          query.
        </p>
      </header>
      <div class="stack">
        ${this.renderTargetPicker()} ${this.renderSuites()} ${this.renderEditor()}
        ${this.renderRunState()} ${this.renderReport()} ${this.renderCaseDrawer()}
        ${this.renderDeleteModal()}
      </div>
    `;
  }

  private renderDeleteModal() {
    const suite = this.confirmDelete;
    return html`<bn-modal
      ?open=${suite !== null}
      tone="destructive"
      heading=${suite ? `Delete the suite ${suite.name}?` : "Delete this suite?"}
      @close=${() => (this.confirmDelete = null)}
    >
      <p>
        The suite and its ${suite?.cases.length ?? 0} case${(suite?.cases.length ?? 0) === 1 ? "" : "s"}
        are removed from the draft. It stops being rendered into the bundle, so it stops running.
      </p>
      <p slot="consequence">
        Its stored reports go with it — a report that names cases nobody can look up is not evidence.
        The raw record of what each run actually executed stays on the jobs, so the history of what
        happened survives.
      </p>
      <bn-button slot="footer" @click=${() => (this.confirmDelete = null)}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        @click=${() => {
          if (suite) void this.removeSuite(suite);
        }}
      >
        Delete suite
      </bn-button>
    </bn-modal>`;
  }

  private renderTargetPicker() {
    return html`<bn-card heading="Run against">
      <div class="row">
        <bn-field class="narrow" label="Bundle">
          <bn-select
            .options=${TARGET_OPTIONS}
            .value=${this.targetKind}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              (this.targetKind = e.detail.value as "draft" | "live" | "generation")}
          ></bn-select>
        </bn-field>
        ${this.targetKind === "generation"
          ? html`<bn-field label="Generation" required>
              <bn-input
                mono
                inputmode="numeric"
                .value=${this.generation}
                @input=${(e: CustomEvent<{ value: string }>) => (this.generation = e.detail.value)}
              ></bn-input>
            </bn-field>`
          : nothing}
      </div>
      <p class="muted">
        The suite ships into the bundle under <code>policies.d/tests/</code>, the one path Sluice's
        loader skips — so running a suite does not change the bundle digest and cannot perturb a
        publish confirmation.
      </p>
    </bn-card>`;
  }

  private renderSuites() {
    if (this.suitesError) return this.renderSuitesError(this.suitesError);
    return html`<bn-card heading="Suites">
      ${this.mayAuthor
        ? html`<bn-button slot="actions" variant="primary" @click=${() => this.startCreate()}>
            New suite
          </bn-button>`
        : nothing}
      <bn-table
        caption="Stored test suites"
        ?loading=${this.suitesLoading}
        .columns=${SUITE_COLUMNS}
        .rows=${this.suiteRows()}
      >
        <div slot="empty">
          <bn-empty-state
            icon="⌸"
            heading="No test suites yet"
            message="Without a suite there is nothing for the pre-publish test gate to run, and a policy change has no regression check. A first suite is usually two cases: one subject who should see their own rows, and one who should be refused."
          >
            ${this.mayAuthor
              ? html`<bn-button slot="actions" variant="primary" @click=${() => this.startCreate()}>
                  Create a suite
                </bn-button>`
              : html`<span slot="actions" class="muted"
                  >Authoring a suite needs the maintainer role on this organization.</span
                >`}
          </bn-empty-state>
        </div>
      </bn-table>
    </bn-card>`;
  }

  private suiteRows(): BnRow[] {
    return this.suites.map((s) => {
      const risky = s.cases.filter((c) => usesRequestFacts(c)).length;
      return {
        id: s.id ?? s.name,
        name: s.name,
        cases: String(s.cases.length),
        gate: s.enabled
          ? html`<bn-badge variant="info">in the gate</bn-badge>`
          : html`<span class="muted">not in the gate</span>`,
        risk:
          risky > 0
            ? html`<bn-badge variant="warning"
                >${risky} case${risky === 1 ? "" : "s"} use request facts</bn-badge
              >`
            : nothing,
        updated: s.updated ? when(s.updated) : "",
        actions: html`<div class="actions">
          <bn-button
            size="sm"
            variant="primary"
            ?loading=${this.running && this.ranSuite?.id === s.id}
            @click=${() => void this.run({ suiteId: s.id, label: s })}
          >
            Run
          </bn-button>
          ${this.mayAuthor
            ? html`<bn-button size="sm" @click=${() => this.startEdit(s)}>Edit</bn-button>
                <bn-button size="sm" variant="ghost" @click=${() => (this.confirmDelete = s)}>
                  Delete
                </bn-button>`
            : nothing}
        </div>`,
      };
    });
  }

  private renderEditor() {
    const d = this.draft;
    if (!d) return nothing;
    return html`<bn-card heading=${d.id ? `Edit suite ${d.name}` : "New suite"}>
      <div class="row">
        <bn-field
          label="Name"
          required
          .error=${this.draftErrors["name"] ?? ""}
          hint="Lower-case letters, digits and hyphens. It becomes the file name in the bundle."
        >
          <bn-input
            mono
            .value=${d.name}
            @input=${(e: CustomEvent<{ value: string }>) =>
              (this.draft = { ...d, name: e.detail.value })}
          ></bn-input>
        </bn-field>
      </div>
      <bn-checkbox
        label="Run this suite in the pre-publish gate"
        hint="The test gate is opt-in per organization: with it on, a failing or zero-case run blocks a publish. With it off, a failure is a warning someone has to acknowledge."
        .checked=${d.enabled}
        @change=${(e: CustomEvent<{ checked: boolean }>) =>
          (this.draft = { ...d, enabled: e.detail.checked })}
      ></bn-checkbox>

      <h2>Cases</h2>
      ${this.caseComplaints().length > 0
        ? html`<div class="alert" role="alert">
            <div class="alert-title">
              ${d.cases.length === 0 ? "This suite has no cases" : "These cases need attention"}
            </div>
            <ul>
              ${this.caseComplaints().map((m) => html`<li>${m}</li>`)}
            </ul>
          </div>`
        : nothing}
      <bn-table
        caption="Cases in this suite"
        .columns=${CASE_COLUMNS}
        .rows=${this.caseRows(d)}
        empty="No cases yet. A suite with no cases runs zero cases, which the runner reports as a failure."
      ></bn-table>
      <div class="actions" style="margin-top: var(--bino-space-md)">
        <bn-button @click=${() => this.openCase(null)}>Add a case</bn-button>
      </div>

      <h2>Schema fixture</h2>
      <p class="muted">
        Three-part table names and their columns. It exists so a <code>SELECT *</code> case can be
        expanded with no live datasource — without it, such a case cannot be evaluated. It is a
        fixture, not introspection: filling it from the cache is a convenience, and the cache may be
        stale.
      </p>
      <div class="repeater">
        ${d.schema.map(
          (row, i) => html`<div class="repeater-row">
            <bn-input
              aria-label=${`Table ${i + 1}`}
              mono
              placeholder="warehouse.public.customers"
              .value=${row.fqn}
              .diagnostic=${this.draftErrors[`schema.${i}`] ?? ""}
              @input=${(e: CustomEvent<{ value: string }>) => {
                const schema = [...d.schema];
                schema[i] = { ...row, fqn: e.detail.value };
                this.draft = { ...d, schema };
              }}
            ></bn-input>
            <bn-input
              aria-label=${`Columns of table ${i + 1}`}
              mono
              placeholder="id email region tenant_id"
              .value=${row.columns}
              @input=${(e: CustomEvent<{ value: string }>) => {
                const schema = [...d.schema];
                schema[i] = { ...row, columns: e.detail.value };
                this.draft = { ...d, schema };
              }}
            ></bn-input>
            <bn-button
              size="sm"
              variant="ghost"
              @click=${() => (this.draft = { ...d, schema: d.schema.filter((_, j) => j !== i) })}
            >
              Remove
            </bn-button>
          </div>`,
        )}
        <div class="actions">
          <bn-button
            size="sm"
            @click=${() => (this.draft = { ...d, schema: [...d.schema, { fqn: "", columns: "" }] })}
          >
            Add a table
          </bn-button>
          <bn-button size="sm" @click=${() => void this.fillSchemaFromCache()}>
            Fill from the schema cache
          </bn-button>
        </div>
      </div>

      ${this.saveError ? this.renderSaveError(this.saveError) : nothing}

      <div class="actions" style="margin-top: var(--bino-space-lg)">
        <bn-button variant="primary" ?loading=${this.saving} @click=${() => void this.saveDraft()}>
          ${d.id ? "Save suite" : "Create suite"}
        </bn-button>
        <bn-button
          ?disabled=${d.cases.length === 0}
          @click=${() => {
            const suite = fromSuiteDraft(d);
            void this.run({ suite, label: suite });
          }}
        >
          Run without saving
        </bn-button>
        <bn-button variant="ghost" @click=${() => (this.draft = null)}>Cancel</bn-button>
      </div>
    </bn-card>`;
  }

  private caseRows(d: SuiteDraft): BnRow[] {
    return d.cases.map((c, i) => ({
      id: c.name,
      name: c.name,
      subject: c.subject,
      asserts: assertedLabels(c).join(", ") || html`<span class="muted">nothing asserted</span>`,
      risk:
        c.useRequest && (c.headers.some((h) => h.key.trim() !== "") || c.userAgent.trim() !== "")
          ? html`<bn-badge variant="warning">request facts</bn-badge>`
          : nothing,
      actions: html`<div class="actions">
        <bn-button size="sm" @click=${() => this.openCase(i)}>Edit</bn-button>
        <bn-button
          size="sm"
          variant="ghost"
          @click=${() => (this.draft = { ...d, cases: d.cases.filter((_, j) => j !== i) })}
        >
          Remove
        </bn-button>
      </div>`,
    }));
  }

  private renderCaseDrawer() {
    const c = this.caseDraft;
    return html`<bn-drawer
      ?open=${c !== null}
      heading=${this.caseIndex === null ? "New case" : "Edit case"}
      .width=${720}
      @close=${() => (this.caseDraft = null)}
    >
      ${c ? this.renderCaseForm(c) : nothing}
      <bn-button slot="footer" variant="primary" @click=${() => this.commitCase()}>
        ${this.caseIndex === null ? "Add case" : "Save case"}
      </bn-button>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.caseDraft = null)}>
        Cancel
      </bn-button>
    </bn-drawer>`;
  }

  private renderCaseForm(c: CaseDraft) {
    const requestSet =
      c.useRequest && (c.headers.some((h) => h.key.trim() !== "") || c.userAgent.trim() !== "");
    return html`
      <bn-field label="Case name" required .error=${this.caseErrors["name"] ?? ""}>
        <bn-input
          .value=${c.name}
          @input=${(e: CustomEvent<{ value: string }>) => this.patchCase({ name: e.detail.value })}
        ></bn-input>
      </bn-field>

      <fieldset>
        <legend>Identity</legend>
        <div class="grid">
          <bn-field label="Subject" required .error=${this.caseErrors["subject"] ?? ""}>
            <bn-input
              .value=${c.subject}
              @input=${(e: CustomEvent<{ value: string }>) =>
                this.patchCase({ subject: e.detail.value })}
            ></bn-input>
          </bn-field>
          <bn-field label="Issuer">
            <bn-input
              .value=${c.issuer}
              @input=${(e: CustomEvent<{ value: string }>) =>
                this.patchCase({ issuer: e.detail.value })}
            ></bn-input>
          </bn-field>
          <bn-field label="Email">
            <bn-input
              type="email"
              .value=${c.email}
              @input=${(e: CustomEvent<{ value: string }>) =>
                this.patchCase({ email: e.detail.value })}
            ></bn-input>
          </bn-field>
          <bn-field
            label="Groups"
            .error=${this.caseErrors["groups"] ?? ""}
            hint="Space-separated. A comma is refused, because the CLI splits the flag on commas."
          >
            <bn-input
              .value=${c.groupsText}
              @input=${(e: CustomEvent<{ value: string }>) =>
                this.patchCase({ groupsText: e.detail.value })}
            ></bn-input>
          </bn-field>
        </div>
        <bn-field
          label="Claims"
          hint="Values are strings: a SubjectBinding delivers claims as strings, so a richer type would test a value the live path cannot produce."
        >
          <div class="repeater" role="group">
            ${c.claims.map(
              (kv, i) => html`<div class="repeater-row">
                <bn-input
                  aria-label=${`Claim ${i + 1} name`}
                  mono
                  .value=${kv.key}
                  .diagnostic=${this.caseErrors[`claims.${i}`] ?? ""}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    this.patchCase({ claims: patchKV(c.claims, i, { key: e.detail.value }) })}
                ></bn-input>
                <bn-input
                  aria-label=${`Claim ${i + 1} value`}
                  .value=${kv.value}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    this.patchCase({ claims: patchKV(c.claims, i, { value: e.detail.value }) })}
                ></bn-input>
                <bn-button
                  size="sm"
                  variant="ghost"
                  @click=${() =>
                    this.patchCase({ claims: c.claims.filter((_, j) => j !== i) })}
                >
                  Remove
                </bn-button>
              </div>`,
            )}
            <div>
              <bn-button
                size="sm"
                @click=${() => this.patchCase({ claims: [...c.claims, { key: "", value: "" }] })}
              >
                Add a claim
              </bn-button>
            </div>
          </div>
        </bn-field>
        <p class="muted">
          The identity is JWT-shaped and cannot be otherwise: a case cannot say that the subject
          authenticated with an API key, so a policy whose selector matches on
          <code>subjects.apiKeys</code> is untestable here (<code>SLUICE-U27</code>).
        </p>
      </fieldset>

      <fieldset>
        <legend>Request facts</legend>
        <bn-checkbox
          label="This case sets request facts"
          hint="Headers and a user agent, as the test harness would see them."
          .checked=${c.useRequest}
          @change=${(e: CustomEvent<{ checked: boolean }>) =>
            this.patchCase({ useRequest: e.detail.checked })}
        ></bn-checkbox>
        ${c.useRequest
          ? html`
              ${requestSet
                ? html`<div class="alert" role="alert">
                    <div class="alert-title">
                      This case will pass here and the same policies will fail in production
                    </div>
                    <p>
                      The test harness populates request facts. The live REST and MCP paths do not
                      (<code>SLUICE-U12</code>), so a policy that reads
                      <code>request.headers</code> or <code>request.userAgent</code> is dead on the
                      live path — and it fails as a <strong>denial</strong>, because a request
                      template that cannot render denies the query. Assert on the identity instead
                      unless you are deliberately testing the harness.
                    </p>
                  </div>`
                : nothing}
              <bn-field label="Headers">
                <div class="repeater" role="group">
                  ${c.headers.map(
                    (kv, i) => html`<div class="repeater-row">
                      <bn-input
                        aria-label=${`Header ${i + 1} name`}
                        mono
                        .value=${kv.key}
                        @input=${(e: CustomEvent<{ value: string }>) =>
                          this.patchCase({
                            headers: patchKV(c.headers, i, { key: e.detail.value }),
                          })}
                      ></bn-input>
                      <bn-input
                        aria-label=${`Header ${i + 1} value`}
                        .value=${kv.value}
                        @input=${(e: CustomEvent<{ value: string }>) =>
                          this.patchCase({
                            headers: patchKV(c.headers, i, { value: e.detail.value }),
                          })}
                      ></bn-input>
                      <bn-button
                        size="sm"
                        variant="ghost"
                        @click=${() =>
                          this.patchCase({ headers: c.headers.filter((_, j) => j !== i) })}
                      >
                        Remove
                      </bn-button>
                    </div>`,
                  )}
                  <div>
                    <bn-button
                      size="sm"
                      @click=${() =>
                        this.patchCase({ headers: [...c.headers, { key: "", value: "" }] })}
                    >
                      Add a header
                    </bn-button>
                  </div>
                </div>
              </bn-field>
              <bn-field label="User agent">
                <bn-input
                  .value=${c.userAgent}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    this.patchCase({ userAgent: e.detail.value })}
                ></bn-input>
              </bn-field>
            `
          : nothing}
      </fieldset>

      <bn-field label="SQL" required .error=${this.caseErrors["sql"] ?? ""}>
        <bn-code
          language="sql"
          label="The statement this case evaluates"
          filename="case.sql"
          .readonly=${false}
          .value=${c.sql}
          @bn-code-change=${(e: CustomEvent<{ value: string }>) =>
            this.patchCase({ sql: e.detail.value })}
        ></bn-code>
      </bn-field>

      <fieldset>
        <legend>Expectations</legend>
        <p class="muted">
          An empty expectation is <strong>not checked</strong> — it is no assertion rather than a
          passing one. The five list expectations compare as sorted sets, so order does not matter
          and both a missing and an extra element are failures.
        </p>
        <div class="grid">
          <bn-field label="Outcome" .error=${this.caseErrors["outcome"] ?? ""}>
            <bn-select
              .options=${OUTCOME_OPTIONS}
              .value=${c.outcome}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                this.patchCase({ outcome: e.detail.value })}
            ></bn-select>
          </bn-field>
          <bn-field label="Denying policy" hint="The name the engine should report as the denier.">
            <bn-input
              .value=${c.denyPolicy}
              @input=${(e: CustomEvent<{ value: string }>) =>
                this.patchCase({ denyPolicy: e.detail.value })}
            ></bn-input>
          </bn-field>
          <bn-field
            label="Error code"
            hint=${this.errorCodes.length === 0
              ? "The form schema could not be read, so this is free text. Check the spelling against the schema."
              : "From Sluice's own error catalogue."}
          >
            ${this.errorCodes.length === 0
              ? html`<bn-input
                  mono
                  .value=${c.errorCode}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    this.patchCase({ errorCode: e.detail.value })}
                ></bn-input>`
              : html`<bn-select
                  filterable
                  .options=${this.errorCodes}
                  .value=${c.errorCode}
                  @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                    this.patchCase({ errorCode: e.detail.value })}
                ></bn-select>`}
          </bn-field>
        </div>
        ${this.renderListField("Row filters", "Table keys that should carry a filter.", c.filters, (v) =>
          this.patchCase({ filters: v }),
        )}
        ${this.renderListField(
          "Column masks",
          "One per row, as table.column=maskType.",
          c.masks,
          (v) => this.patchCase({ masks: v }),
        )}
        ${this.renderListField(
          "Post-query masks",
          "Masks the engine applies after the query runs.",
          c.postMasks,
          (v) => this.patchCase({ postMasks: v }),
        )}
        ${this.renderListField(
          "Applied policies",
          "Policies whose selectors should match.",
          c.applied,
          (v) => this.patchCase({ applied: v }),
        )}
        ${this.renderListField(
          "Rejections",
          "Policies that should refuse the statement.",
          c.rejections,
          (v) => this.patchCase({ rejections: v }),
        )}
        <bn-field
          label="Rewritten SQL"
          hint="Compared whitespace-normalised, so indentation and line breaks never cause a failure."
        >
          <bn-input
            mono
            .value=${c.rewrittenSql}
            @input=${(e: CustomEvent<{ value: string }>) =>
              this.patchCase({ rewrittenSql: e.detail.value })}
          ></bn-input>
        </bn-field>
        ${this.renderListField(
          "Rewritten SQL contains",
          "Fragments the rewritten statement must contain, compared whitespace-normalised.",
          c.rewrittenSqlContains,
          (v) => this.patchCase({ rewrittenSqlContains: v }),
        )}
      </fieldset>
    `;
  }

  /** One value per row, rather than a separator this page would have to define:
   * the values here contain dots, equals signs and quotes, and inventing a
   * delimiter would make some legal value unenterable. */
  private renderListField(
    label: string,
    hint: string,
    values: string[],
    set: (v: string[]) => void,
  ) {
    return html`<bn-field label=${label} hint=${hint}>
      <div class="repeater" role="group">
        ${values.map(
          (v, i) => html`<div class="repeater-row">
            <bn-input
              aria-label=${`${label} ${i + 1}`}
              mono
              .value=${v}
              @input=${(e: CustomEvent<{ value: string }>) => {
                const next = [...values];
                next[i] = e.detail.value;
                set(next);
              }}
            ></bn-input>
            <bn-button
              size="sm"
              variant="ghost"
              @click=${() => set(values.filter((_, j) => j !== i))}
            >
              Remove
            </bn-button>
          </div>`,
        )}
        <div>
          <bn-button size="sm" @click=${() => set([...values, ""])}>Add</bn-button>
        </div>
      </div>
    </bn-field>`;
  }

  private renderRunState() {
    const job = this.job;
    if (!job || !this.running) return nothing;
    return html`<bn-panel eyebrow="Run in progress">
      <div class="actions">
        <bn-spinner
          label=${job.status === "queued"
            ? "Queued — waiting for a slot on the cluster"
            : `Running ${this.ranSuite?.name ?? "the suite"} on the pinned image`}
        ></bn-spinner>
        <a class="inline" href=${hrefFor("jobs", job.id)}>Job ${job.id}</a>
        <bn-button size="sm" @click=${() => void this.cancel()}>Cancel</bn-button>
      </div>
    </bn-panel>`;
  }

  /* ── the report ────────────────────────────────────────────────────────── */

  private renderReport() {
    if (this.runError) return this.renderRunError(this.runError);
    const r = this.result;
    if (!r) return nothing;
    return html`
      <bn-card heading="Report">
        ${this.renderWarnings(r)} ${this.renderVerdict(r)}
        <bn-kv .rows=${this.reportRows(r)}></bn-kv>
      </bn-card>
      ${this.renderFindings(r)} ${this.renderCases(r)} ${this.renderCaveats(r)}
    `;
  }

  /**
   * The request-facts warning, first and full-width. `SC-S709`'s first criterion
   * makes it prominent and the second makes a *passing* run carry the annotation
   * too — a green suite over cases that depend on request facts is not evidence
   * about production, and silence there is read as confidence.
   */
  private renderWarnings(r: TestRunResult) {
    const warnings = r.warnings ?? [];
    if (warnings.length === 0) return nothing;
    return html`<div class="alert" role="alert">
      <div class="alert-title">This suite exercises facts the live path does not populate</div>
      ${warnings.map((w) => html`<p>${w}</p>`)}
    </div>`;
  }

  /** The verdict, with the three exit-3 conditions kept apart. */
  private renderVerdict(r: TestRunResult) {
    if (r.ok) {
      return html`<div class="verdict">
          <bn-badge variant="success">Passed</bn-badge>
          <span class="verdict-line">
            ${r.passed} of ${r.total} case${r.total === 1 ? "" : "s"} passed.
          </span>
        </div>
        ${(r.warnings ?? []).length > 0
          ? html`<p class="muted">
              This pass is qualified — see the warning above. It is not evidence about production.
            </p>`
          : nothing}`;
    }
    if (r.reason === "no_cases") {
      return html`<div class="verdict">
          <bn-badge variant="error">Failed — no cases ran</bn-badge>
        </div>
        <div role="alert">
          <p>
            <strong>Zero test cases ran, and that is a failure rather than an empty pass.</strong>
            <code>policy test</code> exits 3 both when cases fail and when none ran, because a gate
            over zero cases is worthless — and a suite that silently matched nothing is exactly how a
            test gate stops protecting anything without anyone noticing.
          </p>
          <p class="muted">
            The suite reached the runner and the runner found nothing to execute. Check that the suite
            still has cases, and that the bundle you ran against carries this suite's file.
          </p>
        </div>`;
    }
    if (r.reason === "bundle_invalid") {
      return html`<div class="verdict">
          <bn-badge variant="error">Failed — the bundle did not load</bn-badge>
        </div>
        <div role="alert">
          <p>
            Nothing was tested: the policy directory did not load or compile, so the runner never
            reached your cases. The findings below are the loader's, in the same shape validation
            shows them — fix the bundle first, then re-run.
          </p>
          <div class="error-actions">
            <bn-button
              @click=${() => navigate(hrefFor("instances", this.instanceId, "validation"))}
            >
              Open validation
            </bn-button>
          </div>
        </div>`;
    }
    return html`<div class="verdict">
        <bn-badge variant="error">Failed</bn-badge>
        <span class="verdict-line">
          ${r.failed} of ${r.total} case${r.total === 1 ? "" : "s"} failed.
        </span>
      </div>
      <p class="muted">
        The run itself worked. Exit 3 with cases that ran is a correct run reporting a negative
        result, which is why this is a report rather than an error banner.
      </p>`;
  }

  private reportRows(r: TestRunResult): BnKvRow[] {
    const rows: BnKvRow[] = [
      { label: "Suite", value: r.suite_name || "(inline)", copyable: false },
      { label: "Ran against", value: targetLabel(r.target), copyable: false },
    ];
    if (r.target.digest) rows.push({ label: "Bundle digest", value: r.target.digest });
    rows.push({ label: "Total", value: String(r.total), copyable: false });
    rows.push({ label: "Passed", value: String(r.passed), copyable: false });
    rows.push({ label: "Failed", value: String(r.failed), copyable: false });
    if (r.reason) rows.push({ label: "Reason", value: r.reason, copyable: false });
    if (r.sluice_version) rows.push({ label: "Sluice version", value: r.sluice_version });
    rows.push({ label: "Duration", value: `${r.duration_ms} ms`, copyable: false });
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (r.run_id) rows.push({ label: "CLI run", value: r.run_id });
    if (r.test_run_id) rows.push({ label: "Stored report", value: r.test_run_id });
    else if (!r.suite_id) {
      rows.push({
        label: "Stored report",
        value: "none — an inline suite has no history to attach one to",
        copyable: false,
      });
    }
    return rows;
  }

  /** Bundle-level findings, when the directory did not load. Same shape as the
   * validation surface's, because it is the same loader through another command. */
  private renderFindings(r: TestRunResult) {
    const findings = r.findings ?? [];
    if (findings.length === 0) return nothing;
    return html`<bn-card heading=${`Why the bundle did not load (${findings.length})`}>
      ${findings.map((f) => this.renderFinding(f))}
    </bn-card>`;
  }

  private renderFinding(f: ValidationFinding) {
    const anchored = f.kind && f.name;
    return html`<div class="case">
      <div class="case-head">
        <bn-badge .variant=${(f.severity === "warning" ? "warning" : "error") as BnBadgeVariant}>
          ${f.severity}
        </bn-badge>
        <span class="chip">${f.stage}</span>
        ${anchored
          ? html`<a
              class="inline"
              href=${hrefFor("instances", this.instanceId, "policies", f.kind ?? "", f.name ?? "")}
              >${f.kind}/${f.name}</a
            >`
          : html`<span class="muted">bundle-level</span>`}
      </div>
      <p>${f.message}</p>
      <div class="raw">${f.raw}</div>
    </div>`;
  }

  private renderCases(r: TestRunResult) {
    if (r.cases.length === 0) return nothing;
    return html`<bn-card heading="Cases">
      ${r.cases.map((c) => this.renderCase(c))}
    </bn-card>`;
  }

  private renderCase(c: TestCaseResult) {
    const open = this.openCases.has(c.name);
    const diffs = c.diffs ?? [];
    return html`<div class="case">
      <div class="case-head">
        <bn-badge .variant=${(c.passed ? "success" : "error") as BnBadgeVariant}>
          ${c.passed ? "passed" : "failed"}
        </bn-badge>
        <span class="case-name">${c.name}</span>
        ${c.uses_request_facts
          ? html`<bn-badge variant="warning">uses request facts</bn-badge>`
          : nothing}
        ${diffs.length > 0
          ? html`<bn-button
              size="sm"
              variant="ghost"
              @click=${() => this.toggleCase(c.name)}
            >
              ${open ? "Hide" : `Show ${diffs.length} failed assertion${diffs.length === 1 ? "" : "s"}`}
            </bn-button>`
          : nothing}
        ${this.explainCaseButton(c)}
      </div>
      ${c.passed && c.uses_request_facts
        ? html`<p class="muted">
            This case passed on facts the live path never provides, so its pass says nothing about
            production.
          </p>`
        : nothing}
      ${open ? diffs.map((d) => this.renderDiff(d)) : nothing}
    </div>`;
  }

  private toggleCase(name: string) {
    const next = new Set(this.openCases);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.openCases = next;
  }

  /** `SC-S707`'s last criterion: one click from a failing case to the same
   * identity and statement in explain, so the assertion and the decision trace
   * are one step apart. It needs the suite that ran, which is why the report
   * keeps it. */
  private explainCaseButton(c: TestCaseResult) {
    const suiteCase = this.ranSuite?.cases.find((x) => x.name === c.name);
    if (!suiteCase) return nothing;
    const href =
      hrefFor("instances", this.instanceId, "explain") +
      explainPrefill({
        target: this.target,
        subject: {
          user: suiteCase.identity.subject,
          issuer: suiteCase.identity.issuer,
          email: suiteCase.identity.email,
          groups: suiteCase.identity.groups,
          claims: suiteCase.identity.claims,
        },
        sql: suiteCase.sql,
      });
    return html`<a class="inline" href=${href}>Explain this case</a>`;
  }

  /**
   * One failed assertion. The two comparison semantics are shown rather than
   * described: a sorted-set failure lists `missing` and `unexpected` separately
   * and never claims a partial match, and a whitespace-normalised failure shows
   * the normalised forms the runner actually compared so indentation is visibly
   * not the cause.
   */
  private renderDiff(d: AssertionDiff) {
    const isList = (d.want_list ?? d.got_list ?? d.missing ?? d.unexpected) !== undefined;
    return html`<div class="diff">
      <div class="diff-head">
        <span class="chip">${d.assertion}</span>
        <span class="chip">${comparisonLabel(d.comparison)}</span>
        ${d.assertion === "unparsed"
          ? html`<bn-badge variant="warning">not parsed</bn-badge>`
          : nothing}
      </div>
      ${d.assertion === "unparsed"
        ? html`<p class="muted">
            This build could not structure the runner's message, so it is shown verbatim below. The
            runner emits preformatted human text and a future Sluice version may have reworded it —
            the raw line is what it said, and it is never dropped.
          </p>`
        : nothing}
      ${isList
        ? html`<div class="sides">
            <div class="side want">
              <div class="side-label">Expected</div>
              <div class="mono">${(d.want_list ?? []).join("\n") || "—"}</div>
            </div>
            <div class="side got">
              <div class="side-label">Got</div>
              <div class="mono">${(d.got_list ?? []).join("\n") || "—"}</div>
            </div>
            ${(d.missing ?? []).length > 0
              ? html`<div class="side">
                  <div class="side-label">Missing</div>
                  <div class="mono">${(d.missing ?? []).join("\n")}</div>
                </div>`
              : nothing}
            ${(d.unexpected ?? []).length > 0
              ? html`<div class="side">
                  <div class="side-label">Unexpected</div>
                  <div class="mono">${(d.unexpected ?? []).join("\n")}</div>
                </div>`
              : nothing}
          </div>`
        : html`<div class="sides">
            <div class="side want">
              <div class="side-label">Expected</div>
              <div class="mono">${d.want ?? "—"}</div>
            </div>
            <div class="side got">
              <div class="side-label">Got</div>
              <div class="mono">
                ${d.got_absent ? "nothing at all — the rewrite succeeded" : (d.got ?? "—")}
              </div>
            </div>
          </div>`}
      ${d.normalized_want !== undefined || d.normalized_got !== undefined
        ? html`<div class="sides" style="margin-top: var(--bino-space-sm)">
            <div class="side">
              <div class="side-label">Expected, normalised</div>
              <div class="mono">${d.normalized_want ?? "—"}</div>
            </div>
            <div class="side">
              <div class="side-label">Got, normalised</div>
              <div class="mono">${d.normalized_got ?? "—"}</div>
            </div>
          </div>`
        : nothing}
      ${d.hint ? html`<p class="muted" style="margin-top: var(--bino-space-sm)">${d.hint}</p>` : nothing}
      <div class="raw">${d.raw}</div>
    </div>`;
  }

  private renderCaveats(r: TestRunResult) {
    if (r.caveats.length === 0) return nothing;
    return html`<bn-card heading="What this run does not cover">
      <ul>
        ${r.caveats.map((c) => html`<li>${c}</li>`)}
      </ul>
      <p class="muted">
        The only real coverage for the API-key path is a live call with a real minted key. A suite
        cannot describe one.
      </p>
    </bn-card>`;
  }

  /* ── failures ──────────────────────────────────────────────────────────── */

  /**
   * A run that produced no report. The one condition worth special-casing is
   * `no_test_suite`: exit 1 because the tests path does not exist, which means an
   * instance with no suite rather than a defect — `SC-S708`'s fourth criterion is
   * that the product offers to create one instead of reporting a failure nobody
   * can act on.
   */
  private renderRunError(err: ApiError) {
    const reason = errorDetail<string>(err, "reason");
    if (reason === "no_test_suite") {
      return html`<bn-card heading="This bundle carries no test suite">
        <p>
          The runner found no suite file at the tests path, so there was nothing to run. That is a
          missing suite and not a test failure — the two are different conditions with different
          remedies, and this one is fixed by writing a suite.
        </p>
        <div class="error-actions">
          ${this.mayAuthor
            ? html`<bn-button variant="primary" @click=${() => this.startCreate()}>
                Create a suite
              </bn-button>`
            : html`<span class="muted"
                >Authoring a suite needs the maintainer role on this organization.</span
              >`}
        </div>
      </bn-card>`;
    }
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    if (reason) rows.push({ label: "Reason", value: reason });
    return html`<bn-card heading="The run produced no report">
      <div role="alert">
        <p class="error-message">${err.serverMessage || err.message}</p>
        <bn-kv .rows=${rows}></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p class="muted">
              A job carries no request id — it outlives the request that started it. Quote the job id
              instead.
            </p>`}
        <div class="error-actions">
          ${this.ranSuite
            ? html`<bn-button
                variant="primary"
                @click=${() => {
                  const suite = this.ranSuite;
                  if (!suite) return;
                  void this.run(
                    suite.id ? { suiteId: suite.id, label: suite } : { suite, label: suite },
                  );
                }}
              >
                Try again
              </bn-button>`
            : nothing}
        </div>
      </div>
    </bn-card>`;
  }

  private renderSaveError(err: ApiError) {
    const fields = validationFields(err);
    return html`<div class="alert" role="alert">
      <div class="alert-title">${err.serverMessage || err.message}</div>
      ${fields.length > 0
        ? html`<ul>
            ${fields.map((f) => html`<li><code>${f.path}</code> — ${f.reason}</li>`)}
          </ul>`
        : nothing}
      <p class="muted">
        Error code <code>${err.code}</code>${err.requestId
          ? html` · request ${err.requestId}`
          : nothing}
      </p>
    </div>`;
  }

  private renderSuitesError(err: ApiError) {
    return html`<bn-card heading="Could not load the test suites">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.loadSuites()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  private renderLoadError(err: ApiError) {
    return html`<bn-card heading="Could not load this instance">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }
}

/* ── conversions and small helpers ─────────────────────────────────────────── */

function emptyCaseDraft(): CaseDraft {
  return {
    name: "",
    subject: "",
    issuer: "",
    email: "",
    groupsText: "",
    claims: [],
    useRequest: false,
    headers: [],
    userAgent: "",
    sql: "",
    outcome: "",
    denyPolicy: "",
    errorCode: "",
    rejections: [],
    filters: [],
    masks: [],
    postMasks: [],
    applied: [],
    rewrittenSql: "",
    rewrittenSqlContains: [],
  };
}

function splitGroups(text: string): string[] {
  return text.split(/\s+/).filter((g) => g !== "");
}

function patchKV(rows: KeyValue[], index: number, patch: Partial<KeyValue>): KeyValue[] {
  return rows.map((kv, i) => (i === index ? { ...kv, ...patch } : kv));
}

function toSuiteDraft(s: TestSuite): SuiteDraft {
  return {
    id: s.id,
    name: s.name,
    enabled: s.enabled,
    schema: Object.entries(s.schema ?? {}).map(([fqn, cols]) => ({
      fqn,
      columns: cols.join(" "),
    })),
    cases: s.cases.map(toCaseDraft),
  };
}

function toCaseDraft(c: SuiteCase): CaseDraft {
  const d = emptyCaseDraft();
  d.name = c.name;
  d.subject = c.identity.subject;
  d.issuer = c.identity.issuer ?? "";
  d.email = c.identity.email ?? "";
  d.groupsText = (c.identity.groups ?? []).join(" ");
  d.claims = Object.entries(c.identity.claims ?? {}).map(([key, value]) => ({ key, value }));
  d.useRequest = c.request !== undefined;
  d.headers = Object.entries(c.request?.headers ?? {}).map(([key, value]) => ({ key, value }));
  d.userAgent = c.request?.userAgent ?? "";
  d.sql = c.sql;
  d.outcome = c.expect.outcome ?? "";
  d.denyPolicy = c.expect.denyPolicy ?? "";
  d.errorCode = c.expect.errorCode ?? "";
  d.rejections = [...(c.expect.rejections ?? [])];
  d.filters = [...(c.expect.filters ?? [])];
  d.masks = [...(c.expect.masks ?? [])];
  d.postMasks = [...(c.expect.postMasks ?? [])];
  d.applied = [...(c.expect.applied ?? [])];
  d.rewrittenSql = c.expect.rewrittenSql ?? "";
  d.rewrittenSqlContains = [...(c.expect.rewrittenSqlContains ?? [])];
  return d;
}

function fromSuiteDraft(d: SuiteDraft): TestSuite {
  const schema: Record<string, string[]> = {};
  for (const row of d.schema) {
    const fqn = row.fqn.trim();
    if (fqn === "") continue;
    schema[fqn] = row.columns.split(/\s+/).filter((c) => c !== "");
  }
  const suite: TestSuite = {
    name: d.name.trim(),
    enabled: d.enabled,
    cases: d.cases.map(fromCaseDraft),
  };
  if (d.id) suite.id = d.id;
  if (Object.keys(schema).length > 0) suite.schema = schema;
  return suite;
}

function fromCaseDraft(d: CaseDraft): SuiteCase {
  const claims: Record<string, string> = {};
  for (const kv of d.claims) {
    const key = kv.key.trim();
    if (key !== "") claims[key] = kv.value;
  }
  const headers: Record<string, string> = {};
  for (const kv of d.headers) {
    const key = kv.key.trim();
    if (key !== "") headers[key] = kv.value;
  }
  const groups = splitGroups(d.groupsText);

  const out: SuiteCase = {
    name: d.name.trim(),
    identity: { subject: d.subject.trim() },
    sql: d.sql,
    expect: {},
  };
  if (d.issuer.trim()) out.identity.issuer = d.issuer.trim();
  if (d.email.trim()) out.identity.email = d.email.trim();
  if (groups.length > 0) out.identity.groups = groups;
  if (Object.keys(claims).length > 0) out.identity.claims = claims;
  if (d.useRequest && (Object.keys(headers).length > 0 || d.userAgent.trim() !== "")) {
    out.request = {};
    if (Object.keys(headers).length > 0) out.request.headers = headers;
    if (d.userAgent.trim() !== "") out.request.userAgent = d.userAgent.trim();
  }
  // Only non-empty expectations are sent: an empty one is not checked, and
  // sending it would suggest it was.
  if (d.outcome) out.expect.outcome = d.outcome as SuiteCase["expect"]["outcome"];
  if (d.denyPolicy.trim()) out.expect.denyPolicy = d.denyPolicy.trim();
  if (d.errorCode.trim()) out.expect.errorCode = d.errorCode.trim();
  const lists: Array<[keyof SuiteCase["expect"], string[]]> = [
    ["rejections", d.rejections],
    ["filters", d.filters],
    ["masks", d.masks],
    ["postMasks", d.postMasks],
    ["applied", d.applied],
    ["rewrittenSqlContains", d.rewrittenSqlContains],
  ];
  for (const [key, values] of lists) {
    const kept = values.map((v) => v.trim()).filter((v) => v !== "");
    if (kept.length > 0) {
      (out.expect as Record<string, unknown>)[key as string] = kept;
    }
  }
  if (d.rewrittenSql.trim()) out.expect.rewrittenSql = d.rewrittenSql.trim();
  return out;
}

/** Which expectations a case actually asserts, for the case list. An empty
 * expectation is not an assertion, so it is not listed. */
function assertedLabels(c: CaseDraft): string[] {
  const out: string[] = [];
  if (c.outcome) out.push("outcome");
  if (c.denyPolicy.trim()) out.push("denyPolicy");
  if (c.errorCode.trim()) out.push("errorCode");
  if (c.filters.some((v) => v.trim())) out.push("filters");
  if (c.masks.some((v) => v.trim())) out.push("masks");
  if (c.postMasks.some((v) => v.trim())) out.push("postMasks");
  if (c.applied.some((v) => v.trim())) out.push("applied");
  if (c.rejections.some((v) => v.trim())) out.push("rejections");
  if (c.rewrittenSql.trim()) out.push("rewrittenSql");
  if (c.rewrittenSqlContains.some((v) => v.trim())) out.push("rewrittenSqlContains");
  return out;
}

/** Whether a stored case sets request facts — the predicate behind the badge on
 * the suite list, which is the same one the run report uses. */
function usesRequestFacts(c: SuiteCase): boolean {
  const r = c.request;
  if (!r) return false;
  return Object.keys(r.headers ?? {}).length > 0 || (r.userAgent ?? "") !== "";
}

/** The comparison, in words. It is what a reader has to understand to read the
 * diff, and the label the runner uses is not self-explanatory. */
function comparisonLabel(comparison: string): string {
  switch (comparison) {
    case "sorted_set":
      return "compared as a sorted set";
    case "whitespace_normalized":
      return "whitespace-normalised";
    case "substring":
      return "substring, whitespace-normalised";
    case "equality":
      return "exact match";
    case "error":
      return "an error, not a comparison";
    default:
      return comparison;
  }
}

function targetLabel(t: { kind: string; generation?: number }): string {
  if (t.kind === "generation") return `generation ${t.generation ?? "?"}`;
  if (t.kind === "live") return `live generation${t.generation ? ` (${t.generation})` : ""}`;
  return t.generation ? `draft (generation ${t.generation})` : "draft";
}

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-tests": PageTests;
  }
}
